import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import PlanPricingTabs from '../subscription/PlanPricingTabs';
import PlanDetailView from '../subscription/PlanDetailView';
import PlanFeatureMatrix from '../subscription/PlanFeatureMatrix';
import PlanPriceDisplay from '../subscription/PlanPriceDisplay';

export default function CrmPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [cycle, setCycle] = useState('monthly');
  const [detailPlan, setDetailPlan] = useState(null);

  useEffect(() => {
    api.get('subscription/plans')
      .then((res) => {
        setPlans(res.data?.data?.items || []);
        setLoadError(false);
      })
      .catch(() => {
        setPlans([]);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading plans…</div>;
  }

  return (
    <div className="space-y-6" data-testid="crm-plans">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Pricing Plans</h1>
        <p className="text-muted-foreground">Active subscription plans for school onboarding</p>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
        <Eye className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p>
          View-only access. All active plans are listed below with pricing, limits, and features.
          Contact your administrator to request plan changes.
        </p>
      </div>

      {loadError && (
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            Unable to load pricing plans. Please try again later.
          </CardContent>
        </Card>
      )}

      {!loadError && (
        <>
          <PlanPricingTabs cycle={cycle} onCycleChange={setCycle} className="max-w-xl" />

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {plans.map((plan) => (
              <Card key={plan.id} className={plan.is_popular ? 'border-blue-500 shadow-sm' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    {plan.is_popular && <Badge className="bg-blue-600 text-white shrink-0">Popular</Badge>}
                  </div>
                  {plan.slug === 'free' && (
                    <Badge variant="outline" className="w-fit text-xs">Free tier</Badge>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <PlanPriceDisplay plan={plan} cycle={cycle} size="sm" />
                  <p className="text-sm text-muted-foreground line-clamp-2">{plan.description}</p>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Students: {plan.features?.limits?.max_students ?? '—'}</p>
                    <p>Teachers: {plan.features?.limits?.max_teachers ?? '—'}</p>
                    {plan.trial_days > 0 && <p>Trial: {plan.trial_days} days</p>}
                  </div>
                  <Button variant="outline" size="sm" className="w-full" onClick={() => setDetailPlan(plan)}>
                    View full details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {!loadError && plans.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No active pricing plans found.
          </CardContent>
        </Card>
      )}

      {!loadError && plans.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Plan Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <PlanFeatureMatrix plans={plans.filter((p) => p.slug !== 'free')} />
          </CardContent>
        </Card>
      )}

      <Dialog open={!!detailPlan} onOpenChange={() => setDetailPlan(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Plan Details</DialogTitle>
          </DialogHeader>
          {detailPlan && <PlanDetailView plan={detailPlan} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
